import { FC } from "react";
import { Button, Dialog, Flex, Stack, Text } from "@chakra-ui/react";

import { CASE_FILE_STATUS, CaseDocumentFileModel, CaseDocumentModel } from "@shared/models";
import { FONT_WEIGHT } from "@assets/theme/foundations/textStyle";

import CaseDocumentFileTag from "../CaseDocumentFileTag";
import CaseDocumentProgressBar from "../CaseDocumentProgressBar";
import { getParsingPhase, getParsingProgress, isFailedFile } from "../CaseDocuments.utils";
import { CaseDocumentModalHeader } from "../CaseDocumentModal";

const FILE_STATUS_LABELS: Record<CASE_FILE_STATUS, string> = {
  [CASE_FILE_STATUS.PENDING]: "Queued",
  [CASE_FILE_STATUS.READING]: "Reading…",
  [CASE_FILE_STATUS.EXTRACTING]: "Done",
  [CASE_FILE_STATUS.CHECKING]: "Done",
  [CASE_FILE_STATUS.PROCESSED]: "Done",
  [CASE_FILE_STATUS.FAILED]: "Couldn’t read",
};

const FileStatus: FC<{ file: CaseDocumentFileModel }> = ({ file }) => {
  const isFailed = isFailedFile(file);

  return (
    <Text
      flexShrink={0}
      fontSize="13px"
      fontWeight={isFailed ? FONT_WEIGHT.HEAVY : undefined}
      color={isFailed ? "score.lowText" : "brand.emptyStateText"}
    >
      {FILE_STATUS_LABELS[file.status]}
    </Text>
  );
};

interface MedicalRecordsParsingStepProps {
  caseDocument: CaseDocumentModel;
  onClose: () => void;
}

const MedicalRecordsParsingStep: FC<MedicalRecordsParsingStepProps> = ({ caseDocument, onClose }) => (
  <>
    <CaseDocumentModalHeader
      title="Parsing medical summaries"
      subtitle="You can close this window or leave the page. Parsing keeps running, and the case page shows progress and results."
    />
    <Dialog.Body display="flex" flexDirection="column" gap={5}>
      <Flex direction="column" gap={2}>
        <Flex justify="space-between" fontSize="13px">
          <Text color="brand.textSecondary" _firstLetter={{ textTransform: "uppercase" }}>
            {getParsingPhase(caseDocument)}
          </Text>
          <Text fontWeight={FONT_WEIGHT.HEAVY} color="brand.modalHeaderText">
            {getParsingProgress(caseDocument)}%
          </Text>
        </Flex>
        <CaseDocumentProgressBar value={getParsingProgress(caseDocument)} h="8px" />
      </Flex>
      <Stack gap={2}>
        {caseDocument.files.map((file) => (
          <Flex
            key={file.id}
            align="center"
            gap={3}
            px="14px"
            py="11px"
            borderRadius="8px"
            border="1px solid"
            borderColor="gray.200"
          >
            <CaseDocumentFileTag file={file} />
            <Text truncate flex={1} fontSize="14px" color="brand.modalHeaderText">
              {file.name}
            </Text>
            <FileStatus file={file} />
          </Flex>
        ))}
      </Stack>
    </Dialog.Body>
    <Dialog.Footer mt={2} borderTop="1px solid" borderColor="gray.200">
      <Button variant="neutral" fontSize="14px" onClick={onClose}>
        Close and keep parsing
      </Button>
    </Dialog.Footer>
  </>
);

export default MedicalRecordsParsingStep;
